import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import SearchIcon from "@redotech/redo-icons/search-sm.svg";
import { Provider } from "@redotech/redo-model/order";
import { ProductInfo as ModelProductInfo } from "@redotech/redo-model/product";
import { ProductInfo as StorefrontProductInfo } from "@redotech/shopify-storefront";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { memo, useEffect, useMemo, useState } from "react";
import useDebounce from "usehooks-ts/dist/esm/useDebounce/useDebounce";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import {
  CheckboxValue,
  RedoCheckbox,
} from "../arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { RedoModal } from "../arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableLayout,
  RedoTableLoadingStateContent,
} from "../arbiter-components/tables/redo-table";
import { RedoTablePaddedCell } from "../arbiter-components/tables/redo-table-cells";
import {
  EmptyHeaderCell,
  StandardRedoTableHeaderCell,
} from "../arbiter-components/tables/redo-table-header-cells";
import { RedoTablePagination } from "../arbiter-components/tables/redo-table-pagination";
import { Flex } from "../flex";
import { ColumnAlignment } from "../table";
import { Text } from "../text";

export interface ProductSelectionLimits {
  maxProducts: number;
  currentSelectedCount: number;
}

export const ProductSelectionModalEntry = memo(
  function ProductSelectionModalEntry<
    T extends {
      productId: string;
      productTitle?: string;
      variantIds: string[];
      variants: ModalVariants[];
      productVariants?: ModalVariants[];
      productImage?: { src: string | null; altText: string | null };
    },
    U extends ModalProduct,
  >({
    productSelectionChange,
    customAddProductElement,
    exactlyOneVariant = false,
    forceSelectFirstVariant = false,
    searchProducts,
    multiselect = false,
    selectionLimits,
    provider,
    alreadySelectedProducts,
  }: {
    productSelectionChange: (value: T[]) => void;
    customAddProductElement?: (props: {
      onAdd: () => void;
      disabled?: boolean;
    }) => React.ReactNode;
    exactlyOneVariant?: boolean;
    forceSelectFirstVariant?: boolean;
    searchProducts: ({
      search,
      signal,
      provider,
    }: {
      search: string;
      signal: AbortSignal;
      provider?: Provider;
    }) => Promise<U[] | undefined>;
    multiselect?: boolean;
    selectionLimits?: ProductSelectionLimits;
    provider?: Provider;
    alreadySelectedProducts?: { product: ModalProduct; variantId: string }[];
  }) {
    const [modalOpen, setModalOpen] = useState(false);

    // Calculate if we're at the limit
    const isAtSelectionLimit = selectionLimits
      ? selectionLimits.currentSelectedCount >= selectionLimits.maxProducts
      : false;

    return (
      <Flex dir="column" gap="sm">
        {isAtSelectionLimit && (
          <Text fontSize="xs" fontWeight="regular" textColor="warning">
            Maximum number of products reached
          </Text>
        )}
        {customAddProductElement ? (
          customAddProductElement({
            onAdd: () => setModalOpen(true),
            disabled: isAtSelectionLimit,
          })
        ) : (
          <RedoButton
            disabled={isAtSelectionLimit}
            hierarchy="secondary"
            onClick={() => setModalOpen(true)}
            size="sm"
            text="Add product"
          />
        )}
        <ProductSelectionModal
          alreadySelectedProducts={alreadySelectedProducts}
          exactlyOneVariant={exactlyOneVariant}
          forceSelectFirstVariant={forceSelectFirstVariant}
          multiselect={multiselect}
          open={modalOpen}
          provider={provider}
          searchProducts={searchProducts}
          selectionLimits={selectionLimits}
          setOpen={setModalOpen}
          setSelected={(selected) => {
            const newSelected = selected.map((item) => ({
              productId: item.product.id,
              productTitle: item.product.title,
              variantIds: item.variants.map((v) => v.admin_graphql_api_id),
              variants: item.variants,
              productVariants: item.product.variants,
              productImage: item.product.image,
            }));

            productSelectionChange(newSelected as T[]);
          }}
        />
      </Flex>
    );
  },
);

export const ProductSelectionModal = memo(function ProductSelectionModal<
  U extends ModalProduct,
>({
  open,
  setOpen,
  setSelected,
  exactlyOneVariant,
  forceSelectFirstVariant = false,
  searchProducts,
  multiselect = false,
  selectionLimits,
  provider,
  alreadySelectedProducts,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  setSelected: (
    selected: { product: ModalProduct; variants: ModalVariants[] }[],
  ) => void;
  exactlyOneVariant?: boolean;
  forceSelectFirstVariant?: boolean;
  searchProducts: ({
    search,
    signal,
    provider,
  }: {
    search: string;
    signal: AbortSignal;
    provider?: Provider;
  }) => Promise<U[] | undefined>;
  multiselect?: boolean;
  selectionLimits?: ProductSelectionLimits;
  provider?: Provider;
  alreadySelectedProducts?: { product: ModalProduct; variantId: string }[];
}) {
  const [searchString, setSearchString] = useState<string>("");
  const debouncedSearch = useDebounce(searchString, 500);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [modalSelected, setModalSelected] = useState<
    { selectedProduct: ModalProduct; selectedVariants: ModalVariants[] }[]
  >([]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Initialize modalSelected with already-selected products when modal opens
  useEffect(() => {
    if (!open) {
      return;
    }

    if (alreadySelectedProducts && alreadySelectedProducts.length > 0) {
      const productMap = new Map<
        string,
        { selectedProduct: ModalProduct; selectedVariants: ModalVariants[] }
      >();

      for (const item of alreadySelectedProducts) {
        const existing = productMap.get(item.product.id);
        const selectedVariant = item.product.variants.find(
          (v) =>
            parseNumericIdFromShopifyId(v.admin_graphql_api_id) ===
            parseNumericIdFromShopifyId(item.variantId),
        );

        if (existing && selectedVariant) {
          existing.selectedVariants.push(selectedVariant);
        } else if (selectedVariant) {
          productMap.set(item.product.id, {
            selectedProduct: item.product,
            selectedVariants: [selectedVariant],
          });
        }
      }

      setModalSelected(Array.from(productMap.values()));
    } else {
      setModalSelected([]);
    }
  }, [open, alreadySelectedProducts]);

  const totalSelectedVariants = useMemo(
    () =>
      modalSelected.reduce(
        (sum, item) => sum + item.selectedVariants.length,
        0,
      ),
    [modalSelected],
  );

  const isAtSelectionLimit = selectionLimits
    ? totalSelectedVariants >= selectionLimits.maxProducts
    : false;

  const productsLoad = useLoad<ModalProduct[]>(
    async function (signal) {
      if (!open) {
        return [];
      }
      const results = await searchProducts({
        search: debouncedSearch,
        provider,
        signal,
      });
      return results ?? [];
    },
    [debouncedSearch, open],
  );

  // Build the unified list: selected products first, then unselected search results
  const allDisplayData = useMemo(() => {
    const selectedIds = new Set(
      modalSelected.map((s) =>
        parseNumericIdFromShopifyId(s.selectedProduct.id),
      ),
    );
    const selectedProducts = modalSelected.map((s) => s.selectedProduct);
    const unselectedResults = (productsLoad.value ?? []).filter(
      (p) => !selectedIds.has(parseNumericIdFromShopifyId(p.id)),
    );
    return [...selectedProducts, ...unselectedResults];
  }, [productsLoad.value, modalSelected]);

  const displayedPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return allDisplayData.slice(start, start + pageSize);
  }, [allDisplayData, currentPage, pageSize]);

  // Expandable rows
  const toggleRowExpansion = useHandler((productId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  });

  const rowsExpansions = useMemo(() => {
    const expansions: Record<
      number,
      { expanded: boolean; numExpansions: number }
    > = {};
    for (const [index, product] of displayedPageData.entries()) {
      const hasMultiple = product.variants.length > 1;
      const hideVariantUI =
        !hasMultiple || (exactlyOneVariant && forceSelectFirstVariant);
      if (!hideVariantUI) {
        const isExpanded = expandedRows.has(product.id);
        expansions[index] = {
          expanded: isExpanded,
          numExpansions: isExpanded ? product.variants.length : 0,
        };
      }
    }
    return expansions;
  }, [
    displayedPageData,
    expandedRows,
    exactlyOneVariant,
    forceSelectFirstVariant,
  ]);

  // Selection helpers
  const getProductCheckboxValue = useHandler(
    (product: ModalProduct): CheckboxValue => {
      const entry = modalSelected.find(
        (s) => s.selectedProduct.id === product.id,
      );
      if (!entry) {
        return false;
      }
      if (exactlyOneVariant) {
        return true;
      }
      if (entry.selectedVariants.length === product.variants.length) {
        return true;
      }
      if (entry.selectedVariants.length > 0) {
        return "indeterminate";
      }
      return false;
    },
  );

  const isProductCheckboxDisabled = useHandler((product: ModalProduct) => {
    if (multiselect) {
      return (
        isAtSelectionLimit &&
        !modalSelected.some((s) => s.selectedProduct.id === product.id)
      );
    }
    return (
      modalSelected.some((s) => s.selectedProduct.id !== product.id) ||
      (isAtSelectionLimit &&
        !modalSelected.some((s) => s.selectedProduct.id === product.id))
    );
  });

  const toggleProductSelection = useHandler((product: ModalProduct) => {
    if (modalSelected.some((s) => s.selectedProduct.id === product.id)) {
      setModalSelected(
        modalSelected.filter((s) => s.selectedProduct.id !== product.id),
      );
    } else if (!isAtSelectionLimit) {
      setModalSelected([
        ...modalSelected,
        {
          selectedProduct: product,
          selectedVariants: exactlyOneVariant
            ? product.variants.slice(0, 1)
            : product.variants,
        },
      ]);
    }
  });

  const isVariantSelected = useHandler((variant: ModalVariants) => {
    return modalSelected.some((s) =>
      s.selectedVariants.some(
        (v) => v.admin_graphql_api_id === variant.admin_graphql_api_id,
      ),
    );
  });

  const isVariantCheckboxDisabled = useHandler((variant: ModalVariants) => {
    return (
      isAtSelectionLimit &&
      !modalSelected.some((s) =>
        s.selectedVariants.some(
          (v) => v.admin_graphql_api_id === variant.admin_graphql_api_id,
        ),
      )
    );
  });

  const toggleVariantSelection = useHandler(
    (product: ModalProduct, variant: ModalVariants) => {
      const productAlreadySelected = modalSelected.some(
        (s) => s.selectedProduct.id === product.id,
      );

      if (!productAlreadySelected) {
        if (!isAtSelectionLimit) {
          setModalSelected([
            ...modalSelected,
            { selectedProduct: product, selectedVariants: [variant] },
          ]);
        }
        return;
      }

      setModalSelected(
        modalSelected.map((s) => {
          if (s.selectedProduct.id !== product.id) {
            return s;
          }
          if (exactlyOneVariant) {
            return { ...s, selectedVariants: [variant] };
          }
          if (
            s.selectedVariants.some(
              (v) => v.admin_graphql_api_id === variant.admin_graphql_api_id,
            )
          ) {
            return {
              ...s,
              selectedVariants: s.selectedVariants.filter(
                (v) => v.admin_graphql_api_id !== variant.admin_graphql_api_id,
              ),
            };
          }
          if (!isAtSelectionLimit) {
            return { ...s, selectedVariants: [...s.selectedVariants, variant] };
          }
          return s;
        }),
      );
    },
  );

  const handleClose = useHandler(() => {
    setSearchString("");
    setModalSelected([]);
    setExpandedRows(new Set());
    setCurrentPage(1);
    setOpen(false);
  });

  const handleConfirm = useHandler(() => {
    setSelected(
      modalSelected.map((item) => ({
        product: item.selectedProduct,
        variants: item.selectedVariants,
      })),
    );
    setOpen(false);
    setSearchString("");
    setModalSelected([]);
    setExpandedRows(new Set());
    setCurrentPage(1);
  });

  const tableLayout: RedoTableLayout<ModalProduct> = useMemo(
    () => ({
      columns: [
        // Expand column
        {
          key: "expand",
          widthMode: { type: "hug" as const },
          renderHeaderCell: () => <EmptyHeaderCell />,
          renderNormalCell: ({ row }: { row: ModalProduct }) => {
            const hasMultiple = row.variants.length > 1;
            const hideVariantUI =
              !hasMultiple || (exactlyOneVariant && forceSelectFirstVariant);
            if (hideVariantUI) {
              return <RedoTablePaddedCell>{null}</RedoTablePaddedCell>;
            }
            const isExpanded = expandedRows.has(row.id);
            return (
              <RedoTablePaddedCell>
                <Flex
                  align="center"
                  justify="center"
                  onClick={() => toggleRowExpansion(row.id)}
                  style={{ cursor: "pointer" }}
                  w="full"
                >
                  {isExpanded ? (
                    <ChevronDownIcon height={16} width={16} />
                  ) : (
                    <ChevronRightIcon height={16} width={16} />
                  )}
                </Flex>
              </RedoTablePaddedCell>
            );
          },
        },
        // Checkbox column
        {
          key: "checkbox",
          widthMode: { type: "hug" as const },
          renderHeaderCell: () => <EmptyHeaderCell />,
          renderNormalCell: ({ row }: { row: ModalProduct }) => (
            <RedoTablePaddedCell>
              <RedoCheckbox
                disabled={isProductCheckboxDisabled(row)}
                setValue={() => toggleProductSelection(row)}
                size="sm"
                value={getProductCheckboxValue(row)}
              />
            </RedoTablePaddedCell>
          ),
          renderExpandedCell: ({
            row,
            expansionIndex,
          }: {
            row: ModalProduct;
            expansionIndex: number;
          }) => {
            const variant = row.variants[expansionIndex];
            if (!variant) {
              return null;
            }
            return (
              <RedoTablePaddedCell>
                <RedoCheckbox
                  disabled={isVariantCheckboxDisabled(variant)}
                  setValue={() => toggleVariantSelection(row, variant)}
                  size="sm"
                  value={isVariantSelected(variant)}
                />
              </RedoTablePaddedCell>
            );
          },
        },
        // Product name column
        {
          key: "product",
          widthMode: { type: "fill" as const, relativeLength: 4 },
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Product" />
          ),
          renderNormalCell: ({ row }: { row: ModalProduct }) => (
            <RedoTablePaddedCell>
              <Flex align="center" gap="md">
                {row.image.src && (
                  <img
                    alt={row.image.altText || ""}
                    height={32}
                    src={row.image.src}
                    style={{ borderRadius: 4, objectFit: "cover" }}
                    width={32}
                  />
                )}
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  {row.title}
                </Text>
              </Flex>
            </RedoTablePaddedCell>
          ),
          renderExpandedCell: ({
            row,
            expansionIndex,
          }: {
            row: ModalProduct;
            expansionIndex: number;
          }) => {
            const variant = row.variants[expansionIndex];
            if (!variant) {
              return null;
            }
            return (
              <RedoTablePaddedCell>
                <Flex align="center" gap="md" pl="xl">
                  {variant.image.src && (
                    <img
                      alt={variant.image.altText || ""}
                      height={32}
                      src={variant.image.src}
                      style={{ borderRadius: 4, objectFit: "cover" }}
                      width={32}
                    />
                  )}
                  <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                    {variant.title}
                  </Text>
                </Flex>
              </RedoTablePaddedCell>
            );
          },
        },
        // Stock column
        {
          key: "stock",
          widthMode: { type: "fill" as const, relativeLength: 2 },
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Stock" />
          ),
          renderNormalCell: ({ row }: { row: ModalProduct }) => {
            const hasMultiple = row.variants.length > 1;
            const hideVariantUI =
              !hasMultiple || (exactlyOneVariant && forceSelectFirstVariant);
            if (hideVariantUI) {
              return (
                <RedoTablePaddedCell>
                  <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                    {row.variants[0]?.inventory_quantity != null
                      ? `${row.variants[0].inventory_quantity} in stock`
                      : ""}
                  </Text>
                </RedoTablePaddedCell>
              );
            }
            const entry = modalSelected.find(
              (s) => s.selectedProduct.id === row.id,
            );
            const selectedCount = entry?.selectedVariants.length ?? 0;
            const totalCount = row.variants.length;
            return (
              <RedoTablePaddedCell>
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  {selectedCount > 0 && selectedCount < totalCount
                    ? `${selectedCount} of ${totalCount} selected`
                    : `${totalCount} variants`}
                </Text>
              </RedoTablePaddedCell>
            );
          },
          renderExpandedCell: ({
            row,
            expansionIndex,
          }: {
            row: ModalProduct;
            expansionIndex: number;
          }) => {
            const variant = row.variants[expansionIndex];
            if (!variant) {
              return null;
            }
            return (
              <RedoTablePaddedCell>
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  {variant.inventory_quantity != null
                    ? `${variant.inventory_quantity} in stock`
                    : ""}
                </Text>
              </RedoTablePaddedCell>
            );
          },
        },
        // Price column
        {
          key: "price",
          widthMode: { type: "fill" as const, relativeLength: 1 },
          alignment: "right" as const,
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell
              alignment={ColumnAlignment.RIGHT}
              headerText="Price"
            />
          ),
          renderNormalCell: ({ row }: { row: ModalProduct }) => (
            <RedoTablePaddedCell>
              <Flex justify="flex-end" w="full">
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  {`$${parseFloat(row.variants[0]?.price ?? "0").toFixed(2)}`}
                </Text>
              </Flex>
            </RedoTablePaddedCell>
          ),
          renderExpandedCell: ({
            row,
            expansionIndex,
          }: {
            row: ModalProduct;
            expansionIndex: number;
          }) => {
            const variant = row.variants[expansionIndex];
            if (!variant) {
              return null;
            }
            return (
              <RedoTablePaddedCell>
                <Flex justify="flex-end" w="full">
                  <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                    {`$${parseFloat(variant.price).toFixed(2)}`}
                  </Text>
                </Flex>
              </RedoTablePaddedCell>
            );
          },
        },
      ],
    }),
    [
      exactlyOneVariant,
      forceSelectFirstVariant,
      expandedRows,
      toggleRowExpansion,
      getProductCheckboxValue,
      isProductCheckboxDisabled,
      toggleProductSelection,
      isVariantSelected,
      isVariantCheckboxDisabled,
      toggleVariantSelection,
      modalSelected,
    ],
  );

  return (
    <RedoModal
      contentScrollable
      headerBorder
      isOpen={open}
      modalSize="lg"
      noPadding
      onModalCloseRequested={handleClose}
      primaryButton={{
        text:
          totalSelectedVariants === 0
            ? "Add products"
            : `Add ${totalSelectedVariants} product${totalSelectedVariants !== 1 ? "s" : ""}`,
        onClick: handleConfirm,
        disabled: totalSelectedVariants === 0,
      }}
      secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
      title="Add products"
    >
      <Flex dir="column" gap="none" w="full">
        <Flex p="xl" w="full">
          <RedoTextInput
            fullWidth
            IconTrailing={() => <SearchIcon height={12} width={12} />}
            placeholder="Search for a specific product..."
            setValue={setSearchString}
            value={searchString}
          />
        </Flex>
        {isAtSelectionLimit && (
          <Flex pb="sm" px="xl">
            <Text fontSize="xs" fontWeight="regular" textColor="warning">
              Maximum number of products reached
            </Text>
          </Flex>
        )}
        <RedoTable
          layout={tableLayout}
          loadingStateContent={
            productsLoad.pending ? <RedoTableLoadingStateContent /> : undefined
          }
          rowsExpansions={rowsExpansions}
          rowsOfData={displayedPageData}
          size="sm"
        />
        {allDisplayData.length > 0 && (
          <RedoTablePagination
            currentPage={currentPage}
            knownRowCount={allDisplayData.length}
            pageSize={pageSize}
            pageSizes={[10, 20, 50]}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            size="sm"
          />
        )}
      </Flex>
    </RedoModal>
  );
});

export function storefrontProductToModalProduct(
  product: StorefrontProductInfo,
): ModalProduct {
  return {
    ...product,
    image: { src: product.featuredImage?.url ?? "", altText: "" },
    images: product.images.nodes.map((i: any) => ({ src: i.url, altText: "" })),
    variants: product.variants.nodes.map((v: any) => ({
      admin_graphql_api_id: v.id,
      inventory_quantity: v.quantityAvailable ?? 0,
      sku: v.sku,
      title: v.title,
      requires_shipping: v.requiresShipping,
      price: v.price.amount,
      image: { src: v.image?.url ?? "", altText: "" },
      productTitle: product.title,
    })),
  };
}

export function productInfoToModalProduct(
  product: ModelProductInfo,
): ModalProduct {
  return {
    id: product.id,
    title: product.name,
    description: product.description,
    handle: "",
    image: { src: product.image?.url ?? "", altText: product.name },
    images: [{ src: product.image?.url ?? "", altText: "" }],
    tags: product.tags,
    variants: product.variants.map((v) => ({
      admin_graphql_api_id: v.id,
      inventory_quantity: v.quantityAvailable ?? 0,
      sku: v.sku,
      title: v.name,
      price: v.price,
      image: { src: v.image?.url ?? "", altText: v.name },
      productTitle: product.name,
    })),
  };
}

export interface ModalProduct {
  id: string;
  title: string;
  description: string;
  handle: string;
  image: { src: string | null; altText: string | null };
  images: Array<{ src: string | null; altText: string | null }>;
  body_html?: string;
  tags: string[];
  variants: ModalVariants[];
  onlineStoreUrl?: string;
}

export interface ModalVariants {
  admin_graphql_api_id: string;
  inventory_quantity?: number;
  sku?: string | null;
  title: string;
  requires_shipping?: boolean;
  price: string;
  image: { src: string | null; altText: string | null };
  productTitle?: string;
}
